import React, { useState, useEffect, useRef } from "react";
import { Select, MenuItem, FormControl, Autocomplete, TextField, CircularProgress } from "@mui/material";
import { fontFamily, styled } from "@mui/system";
import {
  CveTitleText,
  FetchButton,
  InputField,
} from "../assets/styles/singlecveStyledComponent.js";
import { fetchVendors, fetchVendorData, fetchProductVersions, fetchBrandsProducts } from "../actions/productAction.js";
import { POST_API } from "../utils/api.js";
import { API_DOMAIN } from "../utils/domain.js";
import {Title, Subtitle, CTASection, CTAText, EmailFormWrapper, EmailInput} from "../assets/styles/ContentStyles"

const FormContainer = styled("div")({
  width: "100%",
  maxWidth: "900px",
  margin: "0 auto",
  padding: "20px",
});

const FieldsRow = styled("div")({
  display: "flex",
  gap: "15px",
  alignItems: "flex-start",
  justifyContent: "center",
  marginTop: "25px",
  marginBottom: "20px",
  flexWrap: "wrap",

  "@media (max-width: 425px)": {
    flexDirection: "column",
    alignItems:"center",
    gap: "10px",
  },
});

const FieldWrapper = styled("div")({
  display: "flex",
  flexDirection: "column",
  flex: "1",
  minWidth: "180px",
  maxWidth: "250px",
  

  "@media (max-width: 768px)": {
    maxWidth: "100%",
  },
});

const FieldLabel = styled("p")({
  fontWeight: "600",
  fontSize: "0.875rem",
  fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  color: "#FFF",
  marginBottom: "8px",
  textAlign: "left",
});

const StyledInputField = styled(InputField)({
  marginBottom: "0 !important",
  maxWidth: "100%",

  "& .MuiInputBase-input": {
    textAlign: "left",
    fontSize: "0.875rem",
    padding: "10px 12px",
  },
});

const StyledSelect = styled(Select)({
  width: "100%",
  backgroundColor: "rgba(30, 41, 59, 0.6)",
  backdropFilter: "blur(8px)",
  borderRadius: "12px",
  border: "1px solid rgba(255, 255, 255, 0.15)",
  fontSize: "1rem",
  fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  fontWeight: "500",
  color: "#fff",

  "& .MuiOutlinedInput-notchedOutline": {
    border: "none",
  },
  "&:hover": {
    border: "1px solid rgba(255, 255, 255, 0.3)",
    boxShadow: "0 0 8px rgba(34, 211, 238, 0.3)",
  },
  "&.Mui-focused": {
    border: "1px solid rgba(34, 211, 238, 0.8)",
    boxShadow: "0 0 12px rgba(34, 211, 238, 0.6)",
    background: "rgba(30, 41, 59, 0.8)",
  },
  "&.Mui-disabled": {
    opacity: 0.6,
    "& .MuiSelect-select": {
      color: "#94a3b8 !important",
      WebkitTextFillColor: "#94a3b8 !important",
    },
    "& .MuiSvgIcon-root": {
      color: "#94a3b8",
    },
  },
  "& .MuiSelect-select": {
    padding: "10px 12px",
    fontSize: "1rem",
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    fontWeight: "500",
  },
  "& .MuiSelect-select.MuiSelect-select": {
    color: "#fff",
  },
  "& .MuiSvgIcon-root": {
    color: "#fff",
  },
});

const StyledAutocomplete = styled(Autocomplete)({
  width: "100%",

  "& .MuiOutlinedInput-root": {
    backgroundColor: "rgba(30, 41, 59, 0.6)",
    backdropFilter: "blur(8px)",
    borderRadius: "12px",
    border: "1px solid rgba(255, 255, 255, 0.15)",
    padding: "2px 8px",
    color: "#fff",

    "& fieldset": {
      border: "none",
    },

    "&:hover": {
      border: "1px solid rgba(255, 255, 255, 0.3)",
      boxShadow: "0 0 8px rgba(34, 211, 238, 0.3)",
    },

    "&.Mui-focused": {
      border: "1px solid rgba(34, 211, 238, 0.8)",
      boxShadow: "0 0 12px rgba(34, 211, 238, 0.6)",
      background: "rgba(30, 41, 59, 0.8)",
    },
  },

  "& .MuiInputBase-input": {
    fontSize: "1rem",
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    fontWeight: "500",
    padding: "8px 4px !important",
    color: "#fff",
  },

  "& .MuiSvgIcon-root": {
    color: "#fff",
  },
});

const GetScoreButton = styled(FetchButton)({
  position: "absolute",
  left: "50%",
  bottom: 0,
  transform: "translate(-50%, 50%)",
});

const ScoreDisplayContainer = styled("div")(({ active }) => ({
  textAlign: "center",
  display: active ? "block" : "none",
  animation: active ? "fadeInUp 0.6s ease-out" : "none",
  "@keyframes fadeInUp": {
    from: {
      opacity: 0,
      transform: "translateY(20px)",
    },
    to: {
      opacity: 1,
      transform: "translateY(0)",
    },
  },
}));

const ScoreTitle = styled("h2")({
 fontWeight: "600",
  fontSize: "clamp(1rem, 1.5vw + 0.5rem, 1.5rem)",
  fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  color: "#FFF",
  marginBottom: "clamp(1rem, 2vh, 1.625rem) !important",
});

const GaugeContainer = styled("div")({
  position: "relative",
  width: "320px",
  height: "210px",
  margin: "0 auto",
});

const ScoreValueDisplay = styled("div")({
  position: "absolute",
  bottom: "35px",
  left: "50%",
  transform: "translateX(-50%)",
  textAlign: "center",
});

const ScoreNumber = styled("span")({
  fontSize: "76px",
  fontWeight: "800",
  fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  color: "#fff",
  lineHeight: 1,
  letterSpacing: "-2px",
});

const ScoreMax = styled("span")({
  fontSize: "30px",
  fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  color: "#94a3b8",
  fontWeight: "600",
});

const RiskTier = styled("div")(({ riskLevel }) => {
  const styles = {
    marginTop: "10px",
    padding: "12px 30px",
    borderRadius: "16px",
    fontSize: "18px",
    fontWeight: "700",
    letterSpacing: "0.5px",
    textTransform: "uppercase",
    display: "inline-block",
    transition: "all 0.4s ease",
    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  };

  if (riskLevel === "high") {
    return {
      ...styles,
      background: "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)",
      color: "white",
    };
  } else if (riskLevel === "significant") {
    return {
      ...styles,
      background: "linear-gradient(135deg, #fb923c 0%, #f97316 100%)",
      color: "white",
    };
  } else if (riskLevel === "moderate") {
    return {
      ...styles,
      background: "linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%)",
      color: "white",
    };
  } else {
    return {
      ...styles,
      background: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
      color: "white",
    };
  }
});

const VulnerabilityDetailsContainer = styled("div")({
 marginTop: "40px",
  padding: "50px 40px",
  background: "linear-gradient(135deg, rgba(30, 41, 59, 0.95) 0%, rgba(15, 23, 42, 0.95) 100%)",
  borderRadius: "16px",
  border: "1px solid rgba(255, 255, 255, 0.1)",
  color: "#fff",
  textAlign: "left",
  maxWidth: "900px",
  margin: "40px auto 0",

  "@media (max-width: 768px)": {
    padding: "40px 25px",
    margin: "40px 20px 0", 
  },
});

const VulnerabilityText = styled("p")({
  fontSize: "16px",
  lineHeight: "1.6",
  marginBottom: "20px",
  fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  color: "#e2e8f0",
});

const VulnerabilityTitle = styled("h3")({
  fontSize: "20px",
  fontWeight: "600",
  marginTop: "25px",
  marginBottom: "15px",
  fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  color: "#fff",
});

const CVEList = styled("div")({
  display: "flex",
  flexDirection: "column",
  gap: "12px",
  marginBottom: "30px",
});

const CVEItem = styled("div")({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  padding: "15px 20px",
  background: "rgba(255, 255, 255, 0.05)",
  borderRadius: "12px",
  border: "1px solid rgba(255, 255, 255, 0.1)",
  transition: "all 0.3s ease",
  "&:hover": {
    background: "rgba(255, 255, 255, 0.08)",
    borderColor: "rgba(34, 211, 238, 0.3)",
  },
});

const CVEId = styled("span")({
  fontSize: "16px",
  fontWeight: "600",
  fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  color: "#22d3ee",
});

const CVEScore = styled("span")({
  fontSize: "14px",
  fontWeight: "500",
  fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  color: "#94a3b8",
});

const EmailSection = styled("div")({
  borderRadius: "12px",
  textAlign: "center",
});

const EmailInputWrapper = styled("div")({
  display: "flex",
  gap: "20px",
  justifyContent: "center",
  alignItems: "center",
  marginTop: "15px",
  flexWrap: "wrap",

  "@media (max-width: 600px)": {
    flexDirection: "column",
    width: "100%",
  },
});

const EmailButton = styled("button")({
  padding: "8px 14px",
  background: "#70F0A3",
  color: "#000",
  border: "none",
  borderRadius: "50px",
  fontSize: "14px",
  fontWeight: "600",
  fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  cursor: "pointer",
  transition: "all 0.3s ease",
  maxWidth: "200px",
  lineHeight: "1",
  textAlign: "center",
  "&:hover": {
    transform: "translateY(-2px)",
    boxShadow: "0 8px 16px rgba(34, 211, 238, 0.3)",
  },

  "@media (max-width: 600px)": {
    width: "100%",
    maxWidth: "100%",
  },
});

const ProductDetailsForm = ({ onRiskScoreCalculated }) => {
  const [formData, setFormData] = useState({
    vendor: "",
    product: "",
  });

  const [brandsProductsData, setBrandsProductsData] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [vendorData, setVendorData] = useState(null);
  const [products, setProducts] = useState([]);
  const [selectedProductId, setSelectedProductId] = useState(null);
  const [vendorInput, setVendorInput] = useState("");
  const [showVendorDropdown, setShowVendorDropdown] = useState(false);
  const [loading, setLoading] = useState({
    vendors: false,
    vendorData: false,
    fetchingCVE: false,
    brandsProducts: false,
  });
  const [cveResponse, setCveResponse] = useState(null);
  const [cveError, setCveError] = useState("");
  const debounceTimerRef = useRef(null);

  // Fetch brands and products data on component mount
  useEffect(() => {
    const loadBrandsProducts = async () => {
      setLoading((prev) => ({ ...prev, brandsProducts: true }));
      try {
        const result = await fetchBrandsProducts();

        if (result.success && result.data) {
          setBrandsProductsData(result.data);
          // Extract vendor names for the dropdown
          const vendorNames = result.data.map(item => item.vendor_name);
          setVendors(vendorNames);
          console.log("Loaded vendors:", vendorNames);
        } else {
          setCveError(result.message || "Failed to load brands and products data");
        }
      } catch (error) {
        console.error("Error fetching brands-products:", error);
        setCveError("Failed to load brands and products data");
      } finally {
        setLoading((prev) => ({ ...prev, brandsProducts: false }));
      }
    };

    loadBrandsProducts();
  }, []);

  // ============ COMMENTED OUT: CVE Details API code for dropdowns ============
  // Fetch vendors with debouncing
  // const fetchVendorsDebounced = async (searchTerm) => {
  //   if (!searchTerm || searchTerm.trim().length < 2) {
  //     console.log('Search term too short:', searchTerm);
  //     setVendors([]);
  //     setShowVendorDropdown(false);
  //     return;
  //   }

  //   console.log('🔍 Fetching vendors for:', searchTerm);
  //   setLoading((prev) => ({ ...prev, vendors: true }));

  //   try {
  //     // Call the new CVE Details product search API
  //     const requestBody = {
  //       query: searchTerm,
  //       searchType: "vpv",
  //       exactMatch: false,
  //       resultsPerPage: 100,
  //       page: 1
  //     };

  //     const result = await POST_API(`${API_DOMAIN}/api/v1/cvedetails/product/search`, requestBody);

  //     console.log('📦 Vendor search API response:', result);
  //     console.log('📦 Response data:', result.data);
  //     console.log('📦 Results array:', result.data?.results);

  //     if (result.success && result.data) {
  //       // Check for rate limit error
  //       if (result.data.errors && result.data.errors.error === "Rate limit exceeded") {
  //         const errorMessage = result.data.errors.error_description || "Rate limit exceeded. Please try again in a minute.";
  //         console.error("Rate limit error:", errorMessage);
  //         setCveError(errorMessage);
  //         setVendors([]);
  //         setShowVendorDropdown(false);
  //         return;
  //       }

  //       if (result.data.results) {
  //         // Extract unique vendors from results array
  //         const vendorsMap = new Map();
  //         result.data.results.forEach(item => {
  //           if (item.vendorName && !vendorsMap.has(item.vendorId)) {
  //             vendorsMap.set(item.vendorId, item.vendorName);
  //           }
  //         });

  //         const vendorNames = Array.from(vendorsMap.values());
  //         console.log('✅ Extracted unique vendor names:', vendorNames);

  //         if (vendorNames.length > 0) {
  //           setVendors(vendorNames);
  //           setShowVendorDropdown(true);
  //         } else {
  //           console.log('❌ No vendors in response');
  //           setVendors([]);
  //           setShowVendorDropdown(false);
  //         }
  //       } else {
  //         console.log('❌ No results in response');
  //         setVendors([]);
  //         setShowVendorDropdown(false);
  //       }
  //     } else {
  //       console.log('❌ API call failed or no data');
  //       setVendors([]);
  //       setShowVendorDropdown(false);
  //     }
  //   } catch (error) {
  //     console.error('❌ Error fetching vendors:', error);
  //     setVendors([]);
  //     setShowVendorDropdown(false);
  //   } finally {
  //     setLoading((prev) => ({ ...prev, vendors: false }));
  //   }
  // };

  // Handle vendor input change (no debouncing needed - data already loaded)
  const handleVendorInputChange = (event, newInputValue) => {
    console.log('⌨️ Vendor input changed:', newInputValue);
    setVendorInput(newInputValue);

    // Clear all previous data
    setFormData({
      vendor: "",
      product: "",
    });
    setVendorData(null);
    setProducts([]);
    setCveError("");

    // Clear risk score from parent component
    if (onRiskScoreCalculated) {
      onRiskScoreCalculated(null);
    }
  };

  // ============ COMMENTED OUT: Debounced vendor fetching ============
  // const handleVendorInputChange = (event, newInputValue) => {
  //   console.log('⌨️ Vendor input changed:', newInputValue);
  //   setVendorInput(newInputValue);

  //   // Clear all previous data
  //   setFormData({
  //     vendor: "",
  //     product: "",
  //   });
  //   setVendorData(null);
  //   setProducts([]);
  //   setCveError("");

  //   // Clear risk score from parent component
  //   if (onRiskScoreCalculated) {
  //     onRiskScoreCalculated(null);
  //   }

  //   // Clear previous timer
  //   if (debounceTimerRef.current) {
  //     console.log('⏰ Clearing previous timer');
  //     clearTimeout(debounceTimerRef.current);
  //   }

  //   // Set new timer for debounced search
  //   console.log('⏰ Setting new timer for 500ms');
  //   debounceTimerRef.current = setTimeout(() => {
  //     console.log('⏰ Timer fired! Calling fetchVendorsDebounced');
  //     fetchVendorsDebounced(newInputValue);
  //   }, 500); // 500ms delay
  // };

  // Handle vendor selection
  const handleVendorSelect = (event, newValue) => {
    if (!newValue) {
      setFormData((prev) => ({ ...prev, vendor: "" }));
      setVendorData(null);
      setProducts([]);
      return;
    }

    console.log('Vendor selected:', newValue);
    setFormData((prev) => ({ ...prev, vendor: newValue, product: "" }));
    setShowVendorDropdown(false);

    // Find the selected vendor's data from brandsProductsData
    const selectedVendorData = brandsProductsData.find(
      (item) => item.vendor_name === newValue
    );

    if (selectedVendorData && selectedVendorData.products) {
      console.log('Found products for vendor:', selectedVendorData.products);
      setProducts(selectedVendorData.products);
      setVendorData({ products: selectedVendorData.products });
    } else {
      console.log('No products found for vendor');
      setProducts([]);
      setVendorData(null);
    }
  };

  // ============ COMMENTED OUT: CVE Details API product fetching ============
  // const handleVendorSelect = async (event, newValue) => {
  //   if (!newValue) {
  //     setFormData((prev) => ({ ...prev, vendor: "" }));
  //     setVendorData(null);
  //     setProducts([]);
  //     return;
  //   }

  //   console.log('Vendor selected:', newValue);
  //   setFormData((prev) => ({ ...prev, vendor: newValue, product: "" }));
  //   setShowVendorDropdown(false);
  //   setLoading((prev) => ({ ...prev, vendorData: true }));

  //   try {
  //     // Call the new CVE Details product search API
  //     const requestBody = {
  //       query: newValue,
  //       searchType: "product",
  //       exactMatch: false,
  //       resultsPerPage: 100,
  //       page: 1
  //     };

  //     console.log('Fetching products for vendor:', newValue);
  //     const result = await POST_API(`${API_DOMAIN}/api/v1/cvedetails/product/search`, requestBody);
  //     console.log('Product search API response:', result);

  //     if (result.success && result.data) {
  //       // Check for rate limit error
  //       if (result.data.errors && result.data.errors.error === "Rate limit exceeded") {
  //         const errorMessage = result.data.errors.error_description || "Rate limit exceeded. Please try again in a minute.";
  //         console.error("Rate limit error:", errorMessage);
  //         setCveError(errorMessage);
  //         setVendorData(null);
  //         setProducts([]);
  //         return;
  //       }

  //       if (result.data.results) {
  //         // Store the full results data for later use
  //         setVendorData({ products: result.data.results });

  //         // Extract unique product names
  //         const productNames = result.data.results
  //           .filter(item => item.productName) // Filter out empty product names
  //           .map(item => item.productName);

  //         console.log('Extracted products:', productNames);
  //         setProducts(productNames);
  //       } else {
  //         console.error('No results in response');
  //         setVendorData(null);
  //         setProducts([]);
  //       }
  //     } else {
  //       console.error('Failed to load products:', result.message);
  //       setVendorData(null);
  //       setProducts([]);
  //     }
  //   } catch (error) {
  //     console.error('Error fetching products:', error);
  //     setVendorData(null);
  //     setProducts([]);
  //   } finally {
  //     setLoading((prev) => ({ ...prev, vendorData: false }));
  //   }
  // };

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  const handleChange = (field) => (event) => {
    const value = event.target.value;

    setFormData({
      ...formData,
      [field]: value,
    });

    // Note: Product is now just a string from the brands-products API
    // No productId is needed for the new API structure
    if (field === 'product' && value) {
      console.log('Selected product:', value);
    }

    // ============ COMMENTED OUT: CVE Details API productId logic ============
    // If product is being selected, find and set the productId
    // if (field === 'product' && value) {
    //   const selectedProduct = vendorData?.products?.find(
    //     p => p.productName === value
    //   );

    //   if (selectedProduct && selectedProduct.productId) {
    //     console.log('Selected product:', selectedProduct);
    //     console.log('Product ID:', selectedProduct.productId);
    //     setSelectedProductId(selectedProduct.productId);
    //   } else {
    //     setSelectedProductId(null);
    //   }
    // }
  };

  // Fetch CVE details for a single CVE ID
  const fetchCVEDetails = async (cveId) => {
    try {
      const response = await fetch("http://16.171.68.95:8081/api/v1/first-org/cve/getCveDetails", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          cve_id: cveId,
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      return result;
    } catch (error) {
      console.error(`Error fetching CVE ${cveId}:`, error);
      return null;
    }
  };

  // Calculate average EPSS score from CVE details array
  const calculateAverageEPSS = (cveDetailsArray) => {
    let total = 0;
    let count = 0;

    cveDetailsArray.forEach(cveDetail => {
      if (cveDetail && cveDetail.data && cveDetail.data.epss) {
        const score = parseFloat(cveDetail.data.epss);
        if (!isNaN(score)) {
          total += score;
          count++;
        }
      }
    });

    if (count === 0) return null;
    return (total / count).toFixed(4);
  };

  // Convert EPSS score (0-1 range) to risk score (0-100 range)
  const convertToRiskScore = (epssScore) => {
    const score = parseFloat(epssScore) * 100;
    return Math.round(score);
  };

  // Get risk tier based on score
  const getRiskTier = (score) => {
    if (score >= 81) {
      return { text: 'High Risk', level: 'high' };
    } else if (score >= 61) {
      return { text: 'Significant Risk', level: 'significant' };
    } else if (score >= 41) {
      return { text: 'Moderate Risk', level: 'moderate' };
    } else {
      return { text: 'Low Risk', level: 'low' };
    }
  };

  // const handleGetScore = async () => {
  //   console.log("Form Data:", formData);

  //   // Validate that all fields are selected
  //   if (!formData.vendor || !formData.product) {
  //     setCveError("Please select vendor and product");
  //     return;
  //   }

  //   // Clear previous error and response
  //   setCveError("");
  //   setCveResponse(null);
  //   if (onRiskScoreCalculated) {
  //     onRiskScoreCalculated(null);
  //   }
  //   setLoading((prev) => ({ ...prev, fetchingCVE: true }));

  //   try {
  //     // Check if version has no vulnerabilities
  //     if (!selectedVersionObj || !selectedVersionObj.vulnerabilities || selectedVersionObj.vulnerabilities.length === 0) {
  //       console.log("No CVEs found for this version - showing 0 vulnerability score");

  //       // Create a score object with 0 vulnerabilities
  //       const tier = getRiskTier(0);
  //       const scoreData = {
  //         averageEPSS: "0.0000",
  //         riskScoreValue: 0,
  //         riskTier: tier,
  //         totalCVEs: 0,
  //         cveDetails: [],
  //         vendor: formData.vendor,
  //         product: formData.product,
  //         version: formData.version,
  //       };

  //       if (onRiskScoreCalculated) {
  //         onRiskScoreCalculated(scoreData);
  //       }
  //       setLoading((prev) => ({ ...prev, fetchingCVE: false }));
  //       return;
  //     }

  //     const vulnerabilities = selectedVersionObj.vulnerabilities;
  //     console.log("Fetching CVE details for:", vulnerabilities);

  //     // Fetch details for all CVEs
  //     const cveDetailsPromises = vulnerabilities.map(cveId => fetchCVEDetails(cveId));
  //     const cveDetailsArray = await Promise.all(cveDetailsPromises);

  //     console.log("All CVE Details:", cveDetailsArray);

  //     // Calculate average EPSS score
  //     const averageEPSS = calculateAverageEPSS(cveDetailsArray);

  //     if (averageEPSS === null) {
  //       // If calculation fails, show 0 score instead of error
  //       console.log("Unable to calculate EPSS score - showing 0");
  //       const tier = getRiskTier(0);
  //       const scoreData = {
  //         averageEPSS: "0.0000",
  //         riskScoreValue: 0,
  //         riskTier: tier,
  //         totalCVEs: vulnerabilities.length,
  //         cveDetails: cveDetailsArray,
  //         vendor: formData.vendor,
  //         product: formData.product,
  //         version: formData.version,
  //       };

  //       if (onRiskScoreCalculated) {
  //         onRiskScoreCalculated(scoreData);
  //       }
  //     } else {
  //       const riskScoreValue = convertToRiskScore(averageEPSS);
  //       const tier = getRiskTier(riskScoreValue);

  //       const scoreData = {
  //         averageEPSS,
  //         riskScoreValue,
  //         riskTier: tier,
  //         totalCVEs: vulnerabilities.length,
  //         cveDetails: cveDetailsArray,
  //         vendor: formData.vendor,
  //         product: formData.product,
  //         version: formData.version,
  //       };

  //       console.log("Average EPSS Score:", averageEPSS);
  //       console.log("Risk Score (0-100):", riskScoreValue);
  //       console.log("Risk Tier:", tier);

  //       if (onRiskScoreCalculated) {
  //         onRiskScoreCalculated(scoreData);
  //       }
  //     }
  //   } catch (error) {
  //     console.error("Error fetching CVE details:", error);
  //     setCveError("An error occurred while fetching CVE details");
  //   } finally {
  //     setLoading((prev) => ({ ...prev, fetchingCVE: false }));
  //   }
  // };

  const handleGetScoreV1 = async () => {
    console.log("Form Data:", formData);

    // Validate that vendor and product are selected
    if (!formData.vendor || !formData.product) {
      setCveError("Please select vendor and product");
      return;
    }

    // Get the productId from the selected product
    // if (!selectedProductId) {
    //   setCveError("Product ID not found");
    //   return;
    // }

    // Clear previous error and response
    setCveError("");
    setCveResponse(null);
    if (onRiskScoreCalculated) {
      onRiskScoreCalculated(null);
    }
    setLoading((prev) => ({ ...prev, fetchingCVE: true }));

    try {
      const requestBody = {
        vendorName: formData.vendor,
        productName: formData.product,
        // productId: parseInt(selectedProductId),
        outputFormat: "json",
        pageNumber: 1,
        resultsPerPage: 50
      };

      console.log("Fetching vulnerabilities with:", requestBody);

      const result = await POST_API(`${API_DOMAIN}/api/v1/cvedetails/vulnerabilities/search`, requestBody);
      console.log("Vulnerabilities API response:", result);

      if (result.success && result.data) {
        // Check for rate limit error
        if (result.data.errors && result.data.errors.error === "Rate limit exceeded") {
          const errorMessage = result.data.errors.error_description || "Rate limit exceeded. Please try again in a minute.";
          console.error("Rate limit error:", errorMessage);
          setCveError(errorMessage);
          return;
        }

        const productScore = result.data.productScore || 0;
        const vulnerabilities = result.data.vulnerabilities?.results || [];

        const tier = getRiskTier(productScore);
        const scoreData = {
          riskScoreValue: productScore,
          riskTier: tier,
          totalCVEs: vulnerabilities.length,
          cveDetails: vulnerabilities,
          vendor: formData.vendor,
          product: formData.product,
        };

        console.log("Product Score:", productScore);
        console.log("Total Vulnerabilities:", vulnerabilities.length);
        console.log("Risk Tier:", tier);

        if (onRiskScoreCalculated) {
          onRiskScoreCalculated(scoreData);
        }
      } else {
        setCveError("Failed to fetch vulnerability data");
      }
    } catch (error) {
      console.error("Error fetching vulnerabilities:", error);
      setCveError("An error occurred while fetching vulnerability data");
    } finally {
      setLoading((prev) => ({ ...prev, fetchingCVE: false }));
    }
  };

  return (
    <FormContainer>
      <CveTitleText>Is Your Device at Risk?</CveTitleText>
      <FieldsRow>
        <FieldWrapper>
          <StyledAutocomplete
            options={vendorInput.length > 0 ? vendors : []}
            value={formData.vendor}
            onChange={handleVendorSelect}
            onInputChange={handleVendorInputChange}
            inputValue={vendorInput}
            loading={loading.brandsProducts}
            disableClearable={!vendorInput || vendorInput.length === 0}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Type brand name"
                variant="outlined"
              />
            )}
            noOptionsText={
              vendorInput.length > 0 ? 
              (
                loading.brandsProducts
                ? "Loading brands..."
                : "No brands found"
              ) : "Type brand name"
              // loading.brandsProducts
              //   ? "Loading vendors..."
              //   : "No vendors found"
            }
          />
        </FieldWrapper>

        <FieldWrapper>
          <FormControl fullWidth>
            <StyledSelect
              value={formData.product}
              onChange={handleChange("product")}
              displayEmpty
              disabled={!formData.vendor}
              renderValue={(selected) => {
                if (!selected) {
                  return (
                    <span style={{ color: '#94a3b8' }}>
                      Select Product
                    </span>
                  );
                }
                return selected;
              }}
            >
              <MenuItem value="" disabled>
                Select Product
              </MenuItem>
              {products.map((product, index) => (
                <MenuItem key={index} value={product}>
                  {product}
                </MenuItem>
              ))}
            </StyledSelect>
          </FormControl>
        </FieldWrapper>
      </FieldsRow>

      {cveError && (
        <div style={{ color: '#ff6b6b', textAlign: 'center', marginTop: '10px' }}>
          {cveError}
        </div>
      )}

      <GetScoreButton onClick={handleGetScoreV1} disabled={loading.fetchingCVE}>
        {loading.fetchingCVE ? (
          <>
            <CircularProgress size={16} sx={{ color: '#000', mr: 1 }} />
            Loading...
          </>
        ) : (
          "Scan for Vulnerabilities"
        )}
      </GetScoreButton>
    </FormContainer>
  );
};

// Export styled components for score display
export {
  ScoreDisplayContainer,
  ScoreTitle,
  GaugeContainer,
  ScoreValueDisplay,
  ScoreNumber,
  ScoreMax,
  RiskTier,
  VulnerabilityDetailsContainer,
  VulnerabilityText,
  VulnerabilityTitle,
  CVEList,
  CVEItem,
  CVEId,
  CVEScore,
  EmailSection,
  EmailInputWrapper,
  EmailInput,
  EmailButton,
};

export default ProductDetailsForm;
